import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { ArrowLeft, SlidersHorizontal, X, Bed, Bath, Maximize, MapPin, Check } from 'lucide-react';
import SmartImage from '../components/SmartImage';
import SEO from '../components/SEO';
import { cityToLatLng } from '../utils/spainCoords';

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- Helpers ---------- */
const fmtPriceShort = (p) => {
  if (!p) return '€?';
  if (p >= 1_000_000) return `€${(p / 1_000_000).toFixed(p % 1_000_000 === 0 ? 0 : 1)}M`;
  if (p >= 1000) return `€${Math.round(p / 1000)}K`;
  return `€${p}`;
};

const fmtPriceFull = (p) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(p || 0);

/**
 * Inline-SVG pill marker showing the price — modern Airbnb/Idealista style.
 * The icon is rebuilt per-property because the label varies; cheap enough.
 */
const buildPriceIcon = (price) => {
  const label = fmtPriceShort(price);
  return L.divIcon({
    className: 'casa-price-pin',
    html: `<div style="
      background:#1A1918;
      color:#C5A059;
      padding:5px 10px;
      border-radius:18px;
      font:600 12px/1 'Manrope', sans-serif;
      white-space:nowrap;
      border:2px solid #C5A059;
      box-shadow:0 2px 8px rgba(0,0,0,0.25);
      transform:translateY(-50%);
    ">${label}</div>`,
    iconSize: null,
    iconAnchor: [0, 0],
  });
};

/**
 * Cluster icon: gold circle showing the count.
 */
const clusterIconCreate = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 40 : count < 100 ? 48 : 56;
  return L.divIcon({
    html: `<div style="
      width:${size}px;height:${size}px;
      background:#C5A059;
      color:#1A1918;
      border:3px solid white;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font:700 14px/1 'Manrope', sans-serif;
      box-shadow:0 4px 12px rgba(0,0,0,0.25);
    ">${count}</div>`,
    className: 'casa-cluster',
    iconSize: [size, size],
  });
};

/* ---------- Filter constants (mirror Properties.js so UX feels consistent) ---------- */
const PRICE_BUCKETS = [
  { value: 'all', nl: 'Alle prijzen', en: 'All prices', pl: 'Wszystkie ceny' },
  { value: '0-200000', nl: 'Tot €200.000', en: 'Up to €200,000', pl: 'Do €200.000' },
  { value: '200000-300000', nl: '€200.000 - €300.000', en: '€200,000 - €300,000', pl: '€200.000 - €300.000' },
  { value: '300000-500000', nl: '€300.000 - €500.000', en: '€300,000 - €500,000', pl: '€300.000 - €500.000' },
  { value: '500000-999999999', nl: '€500.000+', en: '€500,000+', pl: '€500.000+' },
];

const TYPE_OPTIONS = ['Appartement', 'Penthouse', 'Villa', 'Geschakelde woning', 'Bungalow'];
const TYPE_LABELS_PL = {
  'Appartement': 'Apartament',
  'Penthouse': 'Penthouse',
  'Villa': 'Willa',
  'Geschakelde woning': 'Dom w zabudowie',
  'Bungalow': 'Bungalow',
};

const REGION_OPTIONS = [
  { value: 'all', nl: 'Alle regio\u2019s', en: 'All regions', pl: 'Wszystkie regiony' },
  { value: 'Costa Blanca', nl: 'Costa Blanca', en: 'Costa Blanca', pl: 'Costa Blanca' },
  { value: 'Costa Calida', nl: 'Costa C\u00e1lida', en: 'Costa C\u00e1lida', pl: 'Costa C\u00e1lida' },
  { value: 'Costa Almeria', nl: 'Costa Almer\u00eda', en: 'Costa Almer\u00eda', pl: 'Costa Almer\u00eda' },
];

const BUILD_OPTIONS = [
  { value: 'all', nl: 'Alles', en: 'All', pl: 'Wszystkie' },
  { value: 'new', nl: 'Nieuwbouw', en: 'New build', pl: 'Nowe budownictwo' },
  { value: 'resale', nl: 'Bestaande bouw', en: 'Resale', pl: 'Rynek wtórny' },
];

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

/**
 * Internal helper that lives INSIDE <MapContainer>. It registers a
 * resize/orientationchange listener and calls `invalidateSize()` so Leaflet
 * recomputes its canvas after the viewport rotates (a notorious Leaflet bug
 * on mobile devices). Also re-invalidates when filters change because the
 * outer page layout may reflow when the empty state banner mounts.
 */
const MapResizer = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    // Defer one paint so the new layout has settled before we measure.
    const onResize = () => requestAnimationFrame(fix);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    // Initial run — covers mount-time CSS layout shifts.
    requestAnimationFrame(fix);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [map]);

  // Whenever filters change (trigger updates), re-invalidate after layout.
  useEffect(() => { requestAnimationFrame(() => map.invalidateSize()); }, [trigger, map]);
  return null;
};

const MapView = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  /* Two filter states: `draft` is what the user is editing, `applied` is what
     the map is actually filtering on. Filters only commit when the user clicks
     "Toepassen" — prevents accidental zero-result wipes after one selection. */
  const initialFilters = { price: 'all', minBeds: 0, type: 'all', region: 'all', build: 'all' };
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  useEffect(() => {
    fetch(`${API}/api/xml/properties?limit=500&lang=pl`)
      .then(r => r.json())
      .then(data => { setProperties(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  /* Apply filters before geocoding so cluster counts reflect the user's choice. */
  const filtered = useMemo(() => {
    return properties.filter(p => {
      if (appliedFilters.price !== 'all') {
        const [min, max] = appliedFilters.price.split('-').map(Number);
        if (p.price < min || p.price > max) return false;
      }
      if (appliedFilters.minBeds > 0 && (p.bedrooms || 0) < appliedFilters.minBeds) return false;
      if (appliedFilters.type !== 'all' && p.type !== appliedFilters.type) return false;
      if (appliedFilters.region !== 'all' && p.region !== appliedFilters.region) return false;
      if (appliedFilters.build === 'new' && !p.new_build) return false;
      if (appliedFilters.build === 'resale' && p.new_build) return false;
      return true;
    });
  }, [properties, appliedFilters]);

  const pinned = useMemo(
    () => filtered
      .map(p => {
        const coords = cityToLatLng(p.location);
        return coords ? { ...p, coords } : null;
      })
      .filter(Boolean),
    [filtered]
  );

  const center = pinned.length > 0 ? pinned[0].coords : [38.3460, -0.4907];

  const updateDraft = (k, v) => setDraftFilters(prev => ({ ...prev, [k]: v }));
  const applyFilters = () => setAppliedFilters(draftFilters);
  const resetFilters = () => {
    setDraftFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setShowMoreFilters(false);
  };

  /* Has the user changed a draft filter that isn't applied yet? Controls the
     visual "dirty" state of the Apply button + a subtle hint to the user. */
  const draftDirty =
    draftFilters.price !== appliedFilters.price ||
    draftFilters.minBeds !== appliedFilters.minBeds ||
    draftFilters.type !== appliedFilters.type ||
    draftFilters.region !== appliedFilters.region ||
    draftFilters.build !== appliedFilters.build;

  const activeFilterCount =
    (appliedFilters.price !== 'all' ? 1 : 0) +
    (appliedFilters.minBeds > 0 ? 1 : 0) +
    (appliedFilters.type !== 'all' ? 1 : 0) +
    (appliedFilters.region !== 'all' ? 1 : 0) +
    (appliedFilters.build !== 'all' ? 1 : 0);

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.propertiesTitle" descriptionKey="seo.propertiesDescription" noindex />

      {/* Slim header — gives more room to the actual map */}
      <div style={styles.topBar}>
        <Link to="/woningen" style={styles.backLink} data-testid="map-back-link">
          <ArrowLeft size={16} /> Widok listy
        </Link>
        <h1 style={styles.title} data-testid="map-title">
          Szukaj na mapie
        </h1>
        <span style={styles.count} data-testid="map-result-count">
          {pinned.length} nieruchomości
        </span>
      </div>

      {/* Sticky filter bar */}
      <div style={styles.filterBar} data-testid="map-filter-bar">
        <div style={styles.filterRow}>
          {/* Primary filter — Price */}
          <select
            value={draftFilters.price}
            onChange={(e) => updateDraft('price', e.target.value)}
            style={styles.select}
            data-testid="map-filter-price"
          >
            {PRICE_BUCKETS.map(b => (
              <option key={b.value} value={b.value}>{b.pl}</option>
            ))}
          </select>

          {/* Primary filter — Bedrooms */}
          <select
            value={draftFilters.minBeds}
            onChange={(e) => updateDraft('minBeds', Number(e.target.value))}
            style={styles.select}
            data-testid="map-filter-beds"
          >
            <option value={0}>Sypialnie (wszystkie)</option>
            {[1, 2, 3, 4, 5].map(n => (
              <option key={n} value={n}>{n}+ sypialni</option>
            ))}
          </select>

          {/* "More filters" toggle */}
          <button
            type="button"
            onClick={() => setShowMoreFilters(v => !v)}
            style={showMoreFilters ? styles.moreBtnActive : styles.moreBtn}
            data-testid="map-toggle-more-filters"
          >
            <SlidersHorizontal size={14} />
            Więcej filtrów
            {activeFilterCount > 0 && (
              <span style={styles.badge}>{activeFilterCount}</span>
            )}
          </button>

          {/* Apply (commit draft → applied). Always visible so the contract
              with the user is obvious: nothing changes until you click here. */}
          <button
            type="button"
            onClick={applyFilters}
            disabled={!draftDirty}
            style={draftDirty ? styles.applyBtn : styles.applyBtnDisabled}
            data-testid="map-apply-filters"
          >
            <Check size={14} />
            Zastosuj
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={resetFilters}
              style={styles.resetBtn}
              data-testid="map-reset-filters"
            >
              <X size={14} /> Wyczyść
            </button>
          )}
        </div>

        {showMoreFilters && (
          <div style={styles.moreFiltersRow} data-testid="map-more-filters-panel">
            <select
              value={draftFilters.type}
              onChange={(e) => updateDraft('type', e.target.value)}
              style={styles.select}
              data-testid="map-filter-type"
            >
              <option value="all">Typ (wszystkie)</option>
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{TYPE_LABELS_PL[t] || t}</option>)}
            </select>

            <select
              value={draftFilters.region}
              onChange={(e) => updateDraft('region', e.target.value)}
              style={styles.select}
              data-testid="map-filter-region"
            >
              {REGION_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>{r.pl}</option>
              ))}
            </select>

            <select
              value={draftFilters.build}
              onChange={(e) => updateDraft('build', e.target.value)}
              style={styles.select}
              data-testid="map-filter-build"
            >
              {BUILD_OPTIONS.map(b => (
                <option key={b.value} value={b.value}>{b.pl}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* The map itself — big, edge-to-edge on mobile, contained on desktop */}
      {loading ? (
        <p style={styles.loadingText}>Ładowanie mapy...</p>
      ) : (
        <div style={styles.mapWrap} data-testid="property-map">
          {/* Empty-state overlay — shown when filters return zero results. We
              keep the map mounted underneath (no remount) so panning back to
              "no filters" feels instant. */}
          {pinned.length === 0 && (
            <div style={styles.emptyOverlay} data-testid="map-empty-state">
              <div style={styles.emptyCard}>
                <MapPin size={32} color="#C5A059" style={{ marginBottom: '0.75rem' }} />
                <h3 style={styles.emptyTitle}>
                  Brak nieruchomości dla tych filtrów
                </h3>
                <p style={styles.emptyText}>
                  Spróbuj mniej restrykcyjnych filtrów lub zobacz naszą pełną ofertę.
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  style={styles.emptyCta}
                  data-testid="map-empty-reset"
                >
                  Wyczyść filtry
                </button>
              </div>
            </div>
          )}
          <MapContainer
            center={center}
            zoom={isMobile() ? 8 : 9}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
          >
            <MapResizer trigger={appliedFilters} />
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={clusterIconCreate}
              maxClusterRadius={55}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
            >
              {pinned.map(p => (
                <Marker
                  key={p.id}
                  position={p.coords}
                  icon={buildPriceIcon(p.price)}
                >
                  <Popup minWidth={260} maxWidth={300}>
                    <div style={styles.popup} data-testid={`map-popup-${p.id}`}>
                      <SmartImage
                        src={p.images?.[0]}
                        alt={p.title}
                        width={400}
                        style={styles.popupImg}
                      />
                      <div style={styles.popupPrice}>{fmtPriceFull(p.price)}</div>
                      <div style={styles.popupTitle}>{p.title}</div>
                      <div style={styles.popupMeta}>
                        <MapPin size={12} style={{ marginRight: '4px' }} />
                        {p.location}
                      </div>
                      <div style={styles.popupSpecs}>
                        <span><Bed size={12} /> {p.bedrooms || '?'}</span>
                        <span><Bath size={12} /> {p.bathrooms || '?'}</span>
                        <span><Maximize size={12} /> {p.surface || '?'}m&sup2;</span>
                      </div>
                      {p.description && (
                        <p style={styles.popupDesc}>
                          {p.description.slice(0, 110)}
                          {p.description.length > 110 ? '\u2026' : ''}
                        </p>
                      )}
                      <Link
                        to={`/woning/${p.id}`}
                        style={styles.popupCta}
                        data-testid={`map-popup-cta-${p.id}`}
                      >
                        Zobacz nieruchomość
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )}

      <p style={styles.footnote}>
        {`${pinned.length} z ${properties.length} nieruchomości na mapie \u00b7 przeciągaj i przybliżaj, aby odkrywać`}
      </p>
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    paddingBottom: '2rem',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1rem 1.5rem',
    backgroundColor: 'white',
    borderBottom: '1px solid #EAE5DD',
    flexWrap: 'wrap',
  },
  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    color: '#C5A059', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
    color: '#1A1918',
    margin: 0,
    fontWeight: 500,
  },
  count: {
    color: '#8C8B88',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  filterBar: {
    backgroundColor: 'white',
    padding: '0.75rem 1.5rem',
    borderBottom: '1px solid #EAE5DD',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  moreFiltersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '0.6rem',
    paddingTop: '0.6rem',
    borderTop: '1px dashed #EAE5DD',
  },
  select: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    outline: 'none',
    minWidth: '140px',
  },
  moreBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    padding: '0.5rem 0.85rem',
    fontSize: '0.85rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    fontWeight: 500,
  },
  moreBtnActive: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#1A1918',
    border: '1px solid #1A1918',
    color: '#C5A059',
    padding: '0.5rem 0.85rem',
    fontSize: '0.85rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    fontWeight: 600,
  },
  badge: {
    backgroundColor: '#C5A059',
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    borderRadius: '999px',
    marginLeft: '0.25rem',
  },
  resetBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#8C8B88',
    padding: '0.5rem 0.4rem',
    fontSize: '0.8rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  applyBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#C5A059',
    border: '1px solid #C5A059',
    color: 'white',
    padding: '0.5rem 1.1rem',
    fontSize: '0.85rem',
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  applyBtnDisabled: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#EAE5DD',
    border: '1px solid #EAE5DD',
    color: '#8C8B88',
    padding: '0.5rem 1.1rem',
    fontSize: '0.85rem',
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'not-allowed',
    marginLeft: 'auto',
  },
  mapWrap: {
    position: 'relative',
    height: 'calc(100vh - 180px)',
    minHeight: '500px',
    width: '100%',
    borderTop: '1px solid #EAE5DD',
  },
  emptyOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(250, 249, 246, 0.85)',
    padding: '1.5rem',
    pointerEvents: 'auto',
  },
  emptyCard: {
    backgroundColor: 'white',
    padding: '2rem 2.25rem',
    maxWidth: '420px',
    textAlign: 'center',
    border: '1px solid #EAE5DD',
    boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
  },
  emptyTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem',
    color: '#1A1918',
    margin: '0 0 0.5rem',
    fontWeight: 500,
  },
  emptyText: {
    color: '#585755',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    margin: '0 0 1.25rem',
  },
  emptyCta: {
    backgroundColor: '#1A1918',
    color: '#C5A059',
    border: 'none',
    padding: '0.7rem 1.5rem',
    fontSize: '0.82rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  loadingText: {
    textAlign: 'center', color: '#8C8B88', padding: '4rem 0',
  },
  footnote: {
    color: '#8C8B88',
    fontSize: '0.78rem',
    marginTop: '0.75rem',
    textAlign: 'center',
    padding: '0 1rem',
  },
  /* Popup */
  popup: {
    minWidth: '240px',
    fontFamily: "'Manrope', sans-serif",
  },
  popupImg: {
    width: '100%',
    height: '140px',
    marginBottom: '0.6rem',
    borderRadius: '4px',
  },
  popupPrice: {
    color: '#C5A059',
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: '0.15rem',
  },
  popupTitle: {
    color: '#1A1918',
    fontFamily: "'Playfair Display', serif",
    fontSize: '0.95rem',
    fontWeight: 500,
    marginBottom: '0.2rem',
    lineHeight: 1.3,
  },
  popupMeta: {
    display: 'inline-flex', alignItems: 'center',
    color: '#585755', fontSize: '0.78rem', marginBottom: '0.5rem',
  },
  popupSpecs: {
    display: 'flex', gap: '0.85rem',
    color: '#585755', fontSize: '0.78rem',
    paddingBottom: '0.55rem',
    borderBottom: '1px solid #EAE5DD',
    marginBottom: '0.55rem',
  },
  popupDesc: {
    color: '#585755',
    fontSize: '0.8rem',
    lineHeight: 1.4,
    margin: '0 0 0.6rem',
  },
  popupCta: {
    display: 'block',
    backgroundColor: '#C5A059',
    color: 'white',
    textAlign: 'center',
    textDecoration: 'none',
    padding: '0.55rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
};

export default MapView;
